import { z } from 'zod'
import { DomainError } from './errors.js'
import { newId } from './ids.js'
import type { NormalizedBoard } from './normalized.js'

/**
 * Structured software-architecture context, attachable to the board, a swimlane, a row, a
 * column and a card. Each owner stores only what was written at that scope; the effective
 * context of a card is resolved from its containing scopes on every read, so a parent edit
 * reaches every descendant without rewriting it.
 *
 * Like card intent, nothing here is generated: Blueprint organises what people wrote.
 */

/** Bumped when the stored shape changes, so a later migration can recognise old values. */
export const CONTEXT_VERSION = 1

/** Resolution order, least to most specific. A later scope wins for scalar values. */
export const CONTEXT_SCOPES = ['board', 'swimlane', 'row', 'column', 'card'] as const
export type ContextScope = (typeof CONTEXT_SCOPES)[number]

export const CONTEXT_SCOPE_LABELS: Record<ContextScope, string> = {
  board: 'Board',
  swimlane: 'Swimlane',
  row: 'Row',
  column: 'Column',
  card: 'Card',
}

export const CONTEXT_LIMITS = {
  scalar: 200,
  responsibility: 2_000,
  item: 500,
  itemCount: 50,
  typedValue: 2_000,
  relationshipCount: 50,
  relationshipText: 1_000,
} as const

export const ARCHITECTURE_LAYERS = [
  'presentation',
  'api',
  'application',
  'domain',
  'infrastructure',
  'data',
  'integration',
] as const

export const COMPONENT_TYPES = [
  'web_app',
  'api',
  'service',
  'worker',
  'library',
  'cli',
  'database',
  'cache',
  'queue',
] as const

export const SECURITY_BOUNDARIES = ['public', 'authenticated', 'internal', 'privileged'] as const

export const CONTEXT_OPTION_LABELS: Record<string, string> = {
  presentation: 'Presentation',
  api: 'API',
  application: 'Application',
  domain: 'Domain',
  infrastructure: 'Infrastructure',
  data: 'Data',
  integration: 'Integration',
  web_app: 'Web app',
  service: 'Service',
  worker: 'Worker',
  library: 'Library',
  cli: 'CLI',
  database: 'Database',
  cache: 'Cache',
  queue: 'Queue / messaging',
  public: 'Public',
  authenticated: 'Authenticated',
  internal: 'Internal',
  privileged: 'Privileged',
}

export function contextOptionLabel(value: string): string {
  return CONTEXT_OPTION_LABELS[value] ?? value
}

// --- field catalogue -------------------------------------------------------

export interface ScalarFieldDefinition {
  key: ScalarKey
  label: string
  /** Allowed values; absent means free text. */
  options?: readonly string[]
  max: number
}

export interface CollectionFieldDefinition {
  key: CollectionKey
  label: string
  /** Prompt section heading. */
  heading: string
}

export const SCALAR_KEYS = [
  'application',
  'architectureLayer',
  'componentType',
  'domain',
  'responsibility',
  'runtime',
  'securityBoundary',
] as const
export type ScalarKey = (typeof SCALAR_KEYS)[number]

export const COLLECTION_KEYS = [
  'technologies',
  'interfaces',
  'dependencies',
  'dataOwnership',
  'constraints',
  'nonFunctional',
  'operational',
  'conventions',
] as const
export type CollectionKey = (typeof COLLECTION_KEYS)[number]

export const SCALAR_FIELDS: readonly ScalarFieldDefinition[] = [
  { key: 'application', label: 'Application', max: CONTEXT_LIMITS.scalar },
  { key: 'architectureLayer', label: 'Architecture layer', options: ARCHITECTURE_LAYERS, max: CONTEXT_LIMITS.scalar },
  { key: 'componentType', label: 'Component type', options: COMPONENT_TYPES, max: CONTEXT_LIMITS.scalar },
  { key: 'domain', label: 'Domain', max: CONTEXT_LIMITS.scalar },
  { key: 'responsibility', label: 'Responsibility', max: CONTEXT_LIMITS.responsibility },
  { key: 'runtime', label: 'Runtime / deployment', max: CONTEXT_LIMITS.scalar },
  { key: 'securityBoundary', label: 'Security boundary', options: SECURITY_BOUNDARIES, max: CONTEXT_LIMITS.scalar },
]

export const COLLECTION_FIELDS: readonly CollectionFieldDefinition[] = [
  { key: 'technologies', label: 'Technologies', heading: 'Technology context' },
  { key: 'interfaces', label: 'Interfaces', heading: 'Interfaces' },
  { key: 'dependencies', label: 'Dependencies', heading: 'Dependencies' },
  { key: 'dataOwnership', label: 'Data ownership', heading: 'Data ownership' },
  { key: 'constraints', label: 'Architectural constraints', heading: 'Architectural constraints' },
  { key: 'nonFunctional', label: 'Non-functional requirements', heading: 'Non-functional requirements' },
  { key: 'operational', label: 'Operational considerations', heading: 'Operational considerations' },
  { key: 'conventions', label: 'Engineering conventions', heading: 'Engineering conventions' },
]

export function scalarField(key: ScalarKey): ScalarFieldDefinition {
  return SCALAR_FIELDS.find((field) => field.key === key)!
}

export function collectionField(key: CollectionKey): CollectionFieldDefinition {
  return COLLECTION_FIELDS.find((field) => field.key === key)!
}

/**
 * Type-specific context. Every field is a scalar resolved with the usual precedence, and
 * each section is independent of the component type, so a worker card under a messaging
 * column still inherits the column's delivery semantics.
 */
export interface TypedSectionDefinition {
  key: string
  label: string
  /** Component types for which the editor offers this section without being asked. */
  componentTypes: readonly string[]
  fields: readonly { key: string; label: string }[]
}

export const TYPED_SECTIONS: readonly TypedSectionDefinition[] = [
  {
    key: 'messaging',
    label: 'Queue / messaging',
    componentTypes: ['queue'],
    fields: [
      { key: 'broker', label: 'Broker' },
      { key: 'exchange', label: 'Exchange / topic' },
      { key: 'queue', label: 'Queue' },
      { key: 'routingKey', label: 'Routing key' },
      { key: 'producer', label: 'Producer' },
      { key: 'consumer', label: 'Consumer' },
      { key: 'deliverySemantics', label: 'Delivery semantics' },
      { key: 'retryPolicy', label: 'Retry policy' },
      { key: 'deadLetter', label: 'Dead-letter policy' },
      { key: 'ordering', label: 'Ordering requirements' },
      { key: 'messageSchema', label: 'Message schema' },
      { key: 'idempotency', label: 'Idempotency requirements' },
    ],
  },
  {
    key: 'database',
    label: 'Database',
    componentTypes: ['database', 'cache'],
    fields: [
      { key: 'engine', label: 'Engine' },
      { key: 'schema', label: 'Schema / database' },
      { key: 'ownership', label: 'Ownership' },
      { key: 'migrations', label: 'Migration mechanism' },
      { key: 'backup', label: 'Backup requirements' },
      { key: 'consistency', label: 'Consistency requirements' },
    ],
  },
  {
    key: 'api',
    label: 'API',
    componentTypes: ['api'],
    fields: [
      { key: 'protocol', label: 'Protocol' },
      { key: 'basePath', label: 'Route / base path' },
      { key: 'authentication', label: 'Authentication' },
      { key: 'versioning', label: 'Versioning' },
      { key: 'consumers', label: 'Consumers' },
      { key: 'rateLimits', label: 'Rate-limit expectations' },
    ],
  },
  {
    key: 'worker',
    label: 'Worker',
    componentTypes: ['worker'],
    fields: [
      { key: 'trigger', label: 'Trigger' },
      { key: 'schedule', label: 'Schedule' },
      { key: 'concurrency', label: 'Concurrency' },
      { key: 'retry', label: 'Retry' },
      { key: 'failureHandling', label: 'Failure handling' },
    ],
  },
]

export function typedSection(key: string): TypedSectionDefinition | undefined {
  return TYPED_SECTIONS.find((section) => section.key === key)
}

/** Unknown keys are kept rather than dropped, so a value from a newer build is not damaged. */
export function typedFieldLabel(sectionKey: string, fieldKey: string): string {
  return typedSection(sectionKey)?.fields.find((field) => field.key === fieldKey)?.label ?? fieldKey
}

// --- schemas ---------------------------------------------------------------

const trimmed = (max: number) =>
  z
    .string()
    .transform((s) => s.trim())
    .pipe(z.string().max(max, `Maximum ${max} characters`))

/** An empty string is "not set" at this scope, so the value is inherited. */
const scalar = (max: number) => trimmed(max).default('')
const choice = (options: readonly [string, ...string[]]) =>
  z.union([z.literal(''), z.enum(options)]).default('')

/** Unique (case-insensitive), order-preserving, empties dropped. */
const itemList = z
  .array(z.string().transform((s) => s.trim()))
  .transform((items) => {
    const seen = new Set<string>()
    const out: string[] = []
    for (const item of items) {
      const key = item.toLowerCase()
      if (item.length === 0 || seen.has(key)) continue
      seen.add(key)
      out.push(item)
    }
    return out
  })
  .pipe(
    z
      .array(z.string().max(CONTEXT_LIMITS.item, `Items are limited to ${CONTEXT_LIMITS.item} characters`))
      .max(CONTEXT_LIMITS.itemCount, `At most ${CONTEXT_LIMITS.itemCount} items`),
  )
  .default([])

// --- source mappings -------------------------------------------------------

export const SOURCE_MAPPING_KINDS = ['repository', 'directory', 'package', 'module', 'file'] as const
export type SourceMappingKind = (typeof SOURCE_MAPPING_KINDS)[number]

/**
 * Where a component's implementation lives. A plain string — how mappings were written
 * before they were structured — is read as a path.
 */
export const sourceMappingSchema = z.preprocess(
  (value) => (typeof value === 'string' ? { path: value } : value),
  z
    .object({
      /** e.g. `github.com/acme/platform`. Empty when the board has one repository. */
      repository: trimmed(300).default(''),
      /** Repository-relative path, e.g. `services/auth/service.py`. */
      path: trimmed(1_000).default(''),
      kind: z.union([z.literal(''), z.enum(SOURCE_MAPPING_KINDS)]).default(''),
      /** Branch, tag or commit the mapping was taken from. */
      ref: trimmed(200).default(''),
    })
    .refine((mapping) => mapping.repository.length > 0 || mapping.path.length > 0, 'A mapping needs a repository or a path'),
)

export type SourceMapping = z.infer<typeof sourceMappingSchema>

/** Identity of a mapping: repository and path, case-insensitive. Kind and ref describe it. */
export function sourceMappingKey(mapping: Pick<SourceMapping, 'repository' | 'path'>): string {
  return `${mapping.repository}|${mapping.path}`.toLowerCase()
}

/** Reads a stored mapping that may still be a plain string. */
export function asSourceMapping(value: SourceMapping | string): SourceMapping {
  return typeof value === 'string' ? { repository: '', path: value.trim(), kind: '', ref: '' } : value
}

export function describeSourceMapping(mapping: SourceMapping): string {
  const location = mapping.repository ? (mapping.path ? `${mapping.repository}:${mapping.path}` : mapping.repository) : mapping.path
  const ref = mapping.ref ? `@${mapping.ref}` : ''
  const kind = mapping.kind ? ` (${mapping.kind})` : ''
  return `${location}${ref}${kind}`
}

/** A caller may name a mapping by its key, by its path, or by the mapping itself. */
export function matchesSourceMapping(identifier: string | Pick<SourceMapping, 'repository' | 'path'>, mapping: SourceMapping): boolean {
  if (typeof identifier !== 'string') return sourceMappingKey(identifier) === sourceMappingKey(mapping)
  const wanted = identifier.trim().toLowerCase()
  return wanted === sourceMappingKey(mapping) || wanted === mapping.path.toLowerCase()
}

const sourceMappingList = z
  .array(sourceMappingSchema)
  .transform((mappings) => {
    const seen = new Set<string>()
    return mappings.filter((mapping) => {
      const key = sourceMappingKey(mapping)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  })
  .pipe(z.array(z.custom<SourceMapping>()).max(CONTEXT_LIMITS.itemCount, `At most ${CONTEXT_LIMITS.itemCount} mappings`))
  .default([])

/** Lists a scope may exclude inherited items from. Source mappings are excluded by key. */
export const EXCLUDABLE_KEYS = [...COLLECTION_KEYS, 'sourceMappings'] as const
export type ExcludableKey = (typeof EXCLUDABLE_KEYS)[number]

export const structuredContextSchema = z.object({
  version: z.number().int().min(1).default(CONTEXT_VERSION),
  application: scalar(CONTEXT_LIMITS.scalar),
  architectureLayer: choice(ARCHITECTURE_LAYERS),
  componentType: choice(COMPONENT_TYPES),
  domain: scalar(CONTEXT_LIMITS.scalar),
  responsibility: scalar(CONTEXT_LIMITS.responsibility),
  runtime: scalar(CONTEXT_LIMITS.scalar),
  securityBoundary: choice(SECURITY_BOUNDARIES),
  technologies: itemList,
  interfaces: itemList,
  dependencies: itemList,
  dataOwnership: itemList,
  constraints: itemList,
  nonFunctional: itemList,
  operational: itemList,
  conventions: itemList,
  sourceMappings: sourceMappingList,
  /**
   * Inherited additive items this scope explicitly suppresses. Never inferred from absence.
   * Source mappings are named by `sourceMappingKey`.
   */
  excluded: z.partialRecord(z.enum(EXCLUDABLE_KEYS), itemList).default({}),
  /** Section key → field key → value. Empty values are "not set". */
  typed: z
    .record(z.string().min(1).max(64), z.record(z.string().min(1).max(64), trimmed(CONTEXT_LIMITS.typedValue)))
    .default({}),
})

export type StructuredContext = z.infer<typeof structuredContextSchema>

export function emptyStructuredContext(): StructuredContext {
  return structuredContextSchema.parse({})
}

// --- relationships ---------------------------------------------------------

export const RELATIONSHIP_TYPES = [
  'depends_on',
  'invokes',
  'routes_to',
  'publishes_to',
  'consumes_from',
  'reads_from',
  'writes_to',
  'implements',
  'extends',
  'contains',
  'owns',
  'deploys_to',
  'authenticates_via',
  'caches_in',
  'monitors',
  'replaces',
  'supersedes',
] as const
export type RelationshipType = (typeof RELATIONSHIP_TYPES)[number]

export const RELATIONSHIP_LABELS: Record<RelationshipType, string> = {
  depends_on: 'Depends on',
  invokes: 'Invokes',
  routes_to: 'Routes to',
  publishes_to: 'Publishes to',
  consumes_from: 'Consumes from',
  reads_from: 'Reads from',
  writes_to: 'Writes to',
  implements: 'Implements',
  extends: 'Extends',
  contains: 'Contains',
  owns: 'Owns',
  deploys_to: 'Deploys to',
  authenticates_via: 'Authenticates via',
  caches_in: 'Caches in',
  monitors: 'Monitors',
  replaces: 'Replaces',
  supersedes: 'Supersedes',
}

export const RELATIONSHIP_MODES = ['sync', 'async'] as const

/**
 * A directed, typed edge from the card that stores it to another card on the same board.
 * Stored on the source card; a target that no longer exists is ignored on read.
 */
export const relationshipSchema = z.object({
  id: z.string().min(1),
  type: z.enum(RELATIONSHIP_TYPES),
  targetId: z.uuid(),
  description: trimmed(CONTEXT_LIMITS.relationshipText).default(''),
  protocol: trimmed(CONTEXT_LIMITS.scalar).default(''),
  /** Message or data contract. */
  contract: trimmed(CONTEXT_LIMITS.relationshipText).default(''),
  mode: z.union([z.literal(''), z.enum(RELATIONSHIP_MODES)]).default(''),
})

export const relationshipsSchema = z.array(relationshipSchema).max(CONTEXT_LIMITS.relationshipCount)

export type Relationship = z.infer<typeof relationshipSchema>

export function newRelationship(targetId: string, type: RelationshipType = 'depends_on'): Relationship {
  return { id: newId(), type, targetId, description: '', protocol: '', contract: '', mode: '' }
}

// --- resolution ------------------------------------------------------------

export interface ContextSource {
  scope: ContextScope
  id: string
  title: string
}

/** One scope's own context. `context` is absent for owners written before this existed. */
export interface ContextLayer {
  source: ContextSource
  context: Partial<StructuredContext> | undefined
}

export interface ResolvedValue {
  value: string
  source: ContextSource
  /** False only when the value was written on the element being resolved. */
  inherited: boolean
  /** Less specific scopes whose value this one replaced, nearest first. */
  overrides: { value: string; source: ContextSource }[]
}

export interface ResolvedItem {
  value: string
  source: ContextSource
  inherited: boolean
}

export interface ExcludedItem {
  value: string
  source: ContextSource
  excludedBy: ContextSource
}

export interface ResolvedMapping {
  value: SourceMapping
  source: ContextSource
  inherited: boolean
}

export interface ExcludedMapping {
  value: SourceMapping
  source: ContextSource
  excludedBy: ContextSource
}

export interface EffectiveContext {
  scalars: Partial<Record<ScalarKey, ResolvedValue>>
  collections: Record<CollectionKey, ResolvedItem[]>
  excluded: Record<CollectionKey, ExcludedItem[]>
  sourceMappings: ResolvedMapping[]
  excludedSourceMappings: ExcludedMapping[]
  /** Section key → field key → resolved value. Only set values appear. */
  typed: Record<string, Record<string, ResolvedValue>>
}

/** Which scopes participate. The element's own scope always does. */
export type ContextScopeSelection = Record<ContextScope, boolean>

export const INHERITED_SELECTION: ContextScopeSelection = {
  board: true,
  swimlane: true,
  row: true,
  column: true,
  card: true,
}

export const CONTEXT_SCOPE_PRESETS: readonly { key: string; label: string; selection: ContextScopeSelection }[] = [
  { key: 'inherited', label: 'Full inherited context', selection: INHERITED_SELECTION },
  {
    key: 'local',
    label: 'Card only',
    selection: { board: false, swimlane: false, row: false, column: false, card: true },
  },
  {
    key: 'row',
    label: 'Card + row',
    selection: { board: false, swimlane: true, row: true, column: false, card: true },
  },
  {
    key: 'column',
    label: 'Card + column',
    selection: { board: false, swimlane: false, row: false, column: true, card: true },
  },
  {
    key: 'row_column',
    label: 'Card + row + column',
    selection: { board: false, swimlane: true, row: true, column: true, card: true },
  },
]

/** Every context owner that a card, or a structural scope, sits inside. */
export type ContextTarget =
  | { scope: 'board' }
  | { scope: 'swimlane'; id: string }
  | { scope: 'row'; id: string }
  | { scope: 'column'; id: string }
  | { scope: 'card'; rowId: string; columnId: string }

/** The containing scopes of `target`, least specific first. The target's own layer is not included. */
export function parentContextLayers(state: NormalizedBoard, target: ContextTarget): ContextLayer[] {
  const layers: ContextLayer[] = []
  if (target.scope === 'board') return layers
  const board = state.board
  layers.push({ source: { scope: 'board', id: board.id, title: board.name }, context: board.context })
  if (target.scope === 'swimlane') return layers
  if (target.scope === 'column') return layers

  const rowId = target.scope === 'row' ? target.id : target.rowId
  const row = state.rows[rowId]
  const swimlane = row ? state.swimlanes[row.swimlaneId] : undefined
  if (swimlane) {
    layers.push({ source: { scope: 'swimlane', id: swimlane.id, title: swimlane.title }, context: swimlane.context })
  }
  if (target.scope === 'row') return layers

  if (row) layers.push({ source: { scope: 'row', id: row.id, title: row.title }, context: row.context })
  const column = state.columns[target.columnId]
  if (column) layers.push({ source: { scope: 'column', id: column.id, title: column.title }, context: column.context })
  return layers
}

/** The layer an element owns itself. */
export function ownContextLayer(state: NormalizedBoard, target: ContextTarget | { scope: 'card'; id: string }): ContextLayer | null {
  switch (target.scope) {
    case 'board':
      return { source: { scope: 'board', id: state.board.id, title: state.board.name }, context: state.board.context }
    case 'swimlane': {
      const swimlane = state.swimlanes[target.id]
      return swimlane ? { source: { scope: 'swimlane', id: swimlane.id, title: swimlane.title }, context: swimlane.context } : null
    }
    case 'row': {
      const row = state.rows[target.id]
      return row ? { source: { scope: 'row', id: row.id, title: row.title }, context: row.context } : null
    }
    case 'column': {
      const column = state.columns[target.id]
      return column ? { source: { scope: 'column', id: column.id, title: column.title }, context: column.context } : null
    }
    case 'card': {
      if (!('id' in target)) return null
      const card = state.cards[target.id]
      return card ? { source: { scope: 'card', id: card.id, title: card.title }, context: card.context } : null
    }
  }
}

/** Drop the parent layers a caller deselected. */
export function selectLayers(layers: readonly ContextLayer[], selection: ContextScopeSelection): ContextLayer[] {
  return layers.filter((layer) => selection[layer.source.scope])
}

function emptyCollections<T>(): Record<CollectionKey, T[]> {
  return Object.fromEntries(COLLECTION_KEYS.map((key) => [key, []])) as unknown as Record<CollectionKey, T[]>
}

/**
 * Resolves effective context: the most specific scalar wins, additive collections merge
 * in scope order, and a scope's exclusions remove matching items inherited from the scopes
 * above it. Items a scope writes itself are never excluded by that same scope.
 */
export function resolveContext(parents: readonly ContextLayer[], own: ContextLayer | null): EffectiveContext {
  const layers = own ? [...parents, own] : [...parents]
  const scalars: Partial<Record<ScalarKey, ResolvedValue>> = {}
  const collections = emptyCollections<ResolvedItem>()
  const excluded = emptyCollections<ExcludedItem>()
  const typed: Record<string, Record<string, ResolvedValue>> = {}
  let sourceMappings: ResolvedMapping[] = []
  const excludedSourceMappings: ExcludedMapping[] = []

  const resolveScalar = (current: ResolvedValue | undefined, value: string, source: ContextSource, inherited: boolean) => {
    if (!current) return { value, source, inherited, overrides: [] }
    return { value, source, inherited, overrides: [{ value: current.value, source: current.source }, ...current.overrides] }
  }

  for (const layer of layers) {
    const context = layer.context
    if (!context) continue
    const inherited = layer !== own

    for (const key of SCALAR_KEYS) {
      const value = (context[key] ?? '').trim()
      if (value.length === 0) continue
      scalars[key] = resolveScalar(scalars[key], value, layer.source, inherited)
    }

    for (const key of COLLECTION_KEYS) {
      const exclusions = new Set((context.excluded?.[key] ?? []).map((item) => item.toLowerCase()))
      if (exclusions.size > 0) {
        const kept: ResolvedItem[] = []
        for (const item of collections[key]) {
          if (exclusions.has(item.value.toLowerCase())) {
            excluded[key].push({ value: item.value, source: item.source, excludedBy: layer.source })
          } else {
            kept.push(item)
          }
        }
        collections[key] = kept
      }
      const present = new Set(collections[key].map((item) => item.value.toLowerCase()))
      for (const raw of context[key] ?? []) {
        const value = raw.trim()
        if (value.length === 0 || present.has(value.toLowerCase())) continue
        present.add(value.toLowerCase())
        collections[key].push({ value, source: layer.source, inherited })
      }
    }

    for (const [sectionKey, fields] of Object.entries(context.typed ?? {})) {
      for (const [fieldKey, raw] of Object.entries(fields)) {
        const value = raw.trim()
        if (value.length === 0) continue
        const section = (typed[sectionKey] ??= {})
        section[fieldKey] = resolveScalar(section[fieldKey], value, layer.source, inherited)
      }
    }

    const mappingExclusions = new Set((context.excluded?.sourceMappings ?? []).map((key) => key.toLowerCase()))
    if (mappingExclusions.size > 0) {
      sourceMappings = sourceMappings.filter((item) => {
        if (!mappingExclusions.has(sourceMappingKey(item.value))) return true
        excludedSourceMappings.push({ value: item.value, source: item.source, excludedBy: layer.source })
        return false
      })
    }
    const presentMappings = new Set(sourceMappings.map((item) => sourceMappingKey(item.value)))
    for (const raw of (context.sourceMappings ?? []) as (SourceMapping | string)[]) {
      const value = asSourceMapping(raw)
      const key = sourceMappingKey(value)
      if (key === '|' || presentMappings.has(key)) continue
      presentMappings.add(key)
      sourceMappings.push({ value, source: layer.source, inherited })
    }
  }

  return { scalars, collections, excluded, typed, sourceMappings, excludedSourceMappings }
}

/** Effective context of a stored card, with the chosen scopes. */
export function cardEffectiveContext(
  state: NormalizedBoard,
  cardId: string,
  selection: ContextScopeSelection = INHERITED_SELECTION,
): EffectiveContext {
  const card = state.cards[cardId]
  if (!card) return resolveContext([], null)
  const parents = parentContextLayers(state, { scope: 'card', rowId: card.rowId, columnId: card.columnId })
  return resolveContext(selectLayers(parents, selection), ownContextLayer(state, { scope: 'card', id: cardId }))
}

export function effectiveContextIsEmpty(context: EffectiveContext): boolean {
  return (
    Object.keys(context.scalars).length === 0 &&
    COLLECTION_KEYS.every((key) => context.collections[key].length === 0) &&
    context.sourceMappings.length === 0 &&
    Object.keys(context.typed).length === 0
  )
}

/** Typed sections worth showing: those matching the component type, plus any holding values. */
export function relevantTypedSections(context: EffectiveContext, extra: readonly string[] = []): string[] {
  const componentType = context.scalars.componentType?.value
  const keys = new Set<string>()
  for (const section of TYPED_SECTIONS) {
    if (componentType && section.componentTypes.includes(componentType)) keys.add(section.key)
  }
  for (const key of Object.keys(context.typed)) keys.add(key)
  for (const key of extra) keys.add(key)
  // Catalogue order first, then any unknown sections in stored order.
  const known = TYPED_SECTIONS.map((section) => section.key).filter((key) => keys.has(key))
  return [...known, ...[...keys].filter((key) => !typedSection(key))]
}

/** Human-readable provenance, e.g. `column "Messaging"`. */
export function describeSource(source: ContextSource): string {
  return `${CONTEXT_SCOPE_LABELS[source.scope].toLowerCase()} "${source.title}"`
}

// --- related components ----------------------------------------------------

export interface RelatedComponent {
  /** Outgoing: this card → other. Incoming: other → this card. */
  direction: 'outgoing' | 'incoming'
  relationship: Relationship
  otherId: string
  otherTitle: string
  otherComponentType: string
  otherResponsibility: string
}

/**
 * Typed relationships touching a card, in both directions. `outgoing` may be an editor
 * draft; incoming edges are read from every other card. Dangling targets are skipped.
 */
export function relatedComponents(
  state: NormalizedBoard,
  cardId: string | null,
  outgoing: readonly Relationship[],
): RelatedComponent[] {
  const describe = (id: string) => {
    const effective = cardEffectiveContext(state, id)
    return {
      otherTitle: state.cards[id]!.title,
      otherComponentType: effective.scalars.componentType?.value ?? '',
      otherResponsibility: effective.scalars.responsibility?.value ?? '',
    }
  }
  const out: RelatedComponent[] = []
  for (const relationship of outgoing) {
    if (!state.cards[relationship.targetId] || relationship.targetId === cardId) continue
    out.push({ direction: 'outgoing', relationship, otherId: relationship.targetId, ...describe(relationship.targetId) })
  }
  if (cardId) {
    for (const other of Object.values(state.cards)) {
      if (other.id === cardId) continue
      for (const relationship of other.relationships ?? []) {
        if (relationship.targetId !== cardId) continue
        out.push({ direction: 'incoming', relationship, otherId: other.id, ...describe(other.id) })
      }
    }
  }
  return out
}

// --- integration views -----------------------------------------------------

export interface ContextProvenanceEntry {
  /** `componentType`, `technologies.RabbitMQ`, `typed.messaging.deliverySemantics`. */
  path: string
  value: string
  scope: ContextScope
  scopeId: string
  scopeTitle: string
  inherited: boolean
  /** Less specific values this one replaced, nearest first. Scalars only. */
  overrides?: { value: string; scope: ContextScope; scopeId: string }[]
}

export interface ContextExclusionEntry {
  path: string
  value: string
  scope: ContextScope
  scopeId: string
  excludedBy: { scope: ContextScope; scopeId: string }
}

/** Effective context as plain values, with provenance kept alongside rather than inline. */
export interface FlatEffectiveContext {
  values: Record<string, unknown>
  provenance: ContextProvenanceEntry[]
  excluded: ContextExclusionEntry[]
}

/**
 * The resolved context in the shape an API or MCP client reads: plain values an agent can
 * use directly, and a separate provenance list saying where each came from.
 */
export function flattenEffectiveContext(effective: EffectiveContext): FlatEffectiveContext {
  const values: Record<string, unknown> = {}
  const provenance: ContextProvenanceEntry[] = []
  const excluded: ContextExclusionEntry[] = []
  const entry = (path: string, value: string, resolved: { source: ContextSource; inherited: boolean }) => ({
    path,
    value,
    scope: resolved.source.scope,
    scopeId: resolved.source.id,
    scopeTitle: resolved.source.title,
    inherited: resolved.inherited,
  })
  const overridesOf = (resolved: ResolvedValue) =>
    resolved.overrides.length > 0
      ? { overrides: resolved.overrides.map((item) => ({ value: item.value, scope: item.source.scope, scopeId: item.source.id })) }
      : {}

  for (const key of SCALAR_KEYS) {
    const resolved = effective.scalars[key]
    if (!resolved) continue
    values[key] = resolved.value
    provenance.push({ ...entry(key, resolved.value, resolved), ...overridesOf(resolved) })
  }
  for (const key of COLLECTION_KEYS) {
    const items = effective.collections[key]
    if (items.length > 0) values[key] = items.map((item) => item.value)
    for (const item of items) provenance.push(entry(`${key}.${item.value}`, item.value, item))
    for (const item of effective.excluded[key]) {
      excluded.push({
        path: `${key}.${item.value}`,
        value: item.value,
        scope: item.source.scope,
        scopeId: item.source.id,
        excludedBy: { scope: item.excludedBy.scope, scopeId: item.excludedBy.id },
      })
    }
  }
  const typed: Record<string, Record<string, string>> = {}
  for (const [sectionKey, fields] of Object.entries(effective.typed)) {
    for (const [fieldKey, resolved] of Object.entries(fields)) {
      ;(typed[sectionKey] ??= {})[fieldKey] = resolved.value
      provenance.push({ ...entry(`typed.${sectionKey}.${fieldKey}`, resolved.value, resolved), ...overridesOf(resolved) })
    }
  }
  if (Object.keys(typed).length > 0) values.typed = typed
  if (effective.sourceMappings.length > 0) values.sourceMappings = effective.sourceMappings.map((item) => item.value)
  for (const item of effective.sourceMappings) {
    provenance.push(entry(`sourceMappings.${sourceMappingKey(item.value)}`, describeSourceMapping(item.value), item))
  }
  for (const item of effective.excludedSourceMappings) {
    excluded.push({
      path: `sourceMappings.${sourceMappingKey(item.value)}`,
      value: describeSourceMapping(item.value),
      scope: item.source.scope,
      scopeId: item.source.id,
      excludedBy: { scope: item.excludedBy.scope, scopeId: item.excludedBy.id },
    })
  }
  return { values, provenance, excluded }
}

const mappingIdentifier = z.union([
  z.string().min(1),
  z.object({ repository: z.string().default(''), path: z.string().default('') }),
])

const itemsByCollection = z.partialRecord(z.enum(COLLECTION_KEYS), z.array(z.string().min(1)).max(CONTEXT_LIMITS.itemCount))

/**
 * An explicit change to the context one scope owns. Each part names what it does, so a
 * caller never has to send (and so never copies) the values that scope inherits.
 */
export const contextPatchSchema = z
  .object({
    /** Scalar values to write here. An empty string removes the local value, restoring inheritance. */
    set: z.partialRecord(z.enum(SCALAR_KEYS), z.string()).optional(),
    /** Items to add to this scope's own lists. */
    add: itemsByCollection.optional(),
    /** Items to remove from this scope's own lists. */
    remove: itemsByCollection.optional(),
    /** Inherited items to suppress at this scope. */
    exclude: itemsByCollection.optional(),
    /** Exclusions to lift, so the inherited items apply again. */
    restore: itemsByCollection.optional(),
    /** Type-specific values, section → field → value. An empty string removes the local value. */
    typed: z.record(z.string(), z.record(z.string(), z.string())).optional(),
    /** Source mappings to add here: {repository, path, kind, ref}, or a plain path. */
    addSourceMappings: z.array(sourceMappingSchema).max(CONTEXT_LIMITS.itemCount).optional(),
    /** This scope's own mappings to remove, named by path, `repository|path`, or {repository, path}. */
    removeSourceMappings: z.array(mappingIdentifier).max(CONTEXT_LIMITS.itemCount).optional(),
    /** Inherited mappings to suppress here, named the same way. */
    excludeSourceMappings: z.array(mappingIdentifier).max(CONTEXT_LIMITS.itemCount).optional(),
    /** Mapping exclusions to lift, named the same way. */
    restoreSourceMappings: z.array(mappingIdentifier).max(CONTEXT_LIMITS.itemCount).optional(),
  })
  .strict()

export type ContextPatch = z.infer<typeof contextPatchSchema>

export interface PatchContextOptions {
  /** Allow writing a value this scope already inherits, pinning it against later parent edits. */
  pinInherited?: boolean
}

/**
 * Applies a patch to the context one scope owns and returns the new local context.
 * Inherited values are read only to validate the patch; they are never copied into the
 * result unless `pinInherited` asks for exactly that. Every problem is reported at once,
 * keyed by the patch path, as a validation error.
 */
export function patchContext(
  local: Partial<StructuredContext> | undefined,
  patch: ContextPatch,
  parents: readonly ContextLayer[],
  options: PatchContextOptions = {},
): StructuredContext {
  const base = structuredContextSchema.parse(local ?? {})
  const inherited = resolveContext(parents, null)
  const errors: Record<string, string> = {}
  const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()
  const inheritedNote = (source: ContextSource) =>
    `Already inherited from ${describeSource(source)}; leave it out so this scope keeps inheriting it, or set pinInherited to pin it here.`

  for (const [key, raw] of Object.entries(patch.set ?? {}) as [ScalarKey, string][]) {
    const value = raw.trim()
    const parent = inherited.scalars[key]
    if (value && parent && same(parent.value, value) && !options.pinInherited) {
      errors[`set.${key}`] = inheritedNote(parent.source)
      continue
    }
    ;(base as Record<ScalarKey, string>)[key] = value
  }

  for (const [key, items] of Object.entries(patch.add ?? {}) as [CollectionKey, string[]][]) {
    for (const raw of items) {
      const value = raw.trim()
      const parent = inherited.collections[key].find((item) => same(item.value, value))
      const excludedHere = (base.excluded[key] ?? []).some((item) => same(item, value))
      if (parent && !excludedHere && !options.pinInherited) {
        errors[`add.${key}.${value}`] = inheritedNote(parent.source)
        continue
      }
      if (!base[key].some((item) => same(item, value))) base[key] = [...base[key], value]
    }
  }

  for (const [key, items] of Object.entries(patch.remove ?? {}) as [CollectionKey, string[]][]) {
    for (const raw of items) {
      const value = raw.trim()
      if (!base[key].some((item) => same(item, value))) {
        errors[`remove.${key}.${value}`] = inherited.collections[key].some((item) => same(item.value, value))
          ? 'This item is inherited, not written at this scope; use exclude to suppress it here.'
          : 'This item is not written at this scope.'
        continue
      }
      base[key] = base[key].filter((item) => !same(item, value))
    }
  }

  for (const [key, items] of Object.entries(patch.exclude ?? {}) as [CollectionKey, string[]][]) {
    for (const raw of items) {
      const value = raw.trim()
      const parent = inherited.collections[key].find((item) => same(item.value, value))
      if (!parent) {
        errors[`exclude.${key}.${value}`] = 'Nothing by that name is inherited here, so there is nothing to exclude.'
        continue
      }
      const current = base.excluded[key] ?? []
      if (!current.some((item) => same(item, value))) base.excluded = { ...base.excluded, [key]: [...current, parent.value] }
    }
  }

  for (const [key, items] of Object.entries(patch.restore ?? {}) as [CollectionKey, string[]][]) {
    for (const raw of items) {
      const value = raw.trim()
      const current = base.excluded[key] ?? []
      if (!current.some((item) => same(item, value))) {
        errors[`restore.${key}.${value}`] = 'This item is not excluded at this scope.'
        continue
      }
      const next = current.filter((item) => !same(item, value))
      const excluded = { ...base.excluded, [key]: next }
      if (next.length === 0) delete excluded[key]
      base.excluded = excluded
    }
  }

  for (const [sectionKey, fields] of Object.entries(patch.typed ?? {})) {
    const section = typedSection(sectionKey)
    if (!section) {
      errors[`typed.${sectionKey}`] = `Unknown section. Supported: ${TYPED_SECTIONS.map((item) => item.key).join(', ')}.`
      continue
    }
    for (const [fieldKey, raw] of Object.entries(fields)) {
      if (!section.fields.some((field) => field.key === fieldKey)) {
        errors[`typed.${sectionKey}.${fieldKey}`] =
          `Unknown field. Supported: ${section.fields.map((field) => field.key).join(', ')}.`
        continue
      }
      const value = raw.trim()
      const parent = inherited.typed[sectionKey]?.[fieldKey]
      if (value && parent && same(parent.value, value) && !options.pinInherited) {
        errors[`typed.${sectionKey}.${fieldKey}`] = inheritedNote(parent.source)
        continue
      }
      const next = { ...(base.typed[sectionKey] ?? {}) }
      if (value) next[fieldKey] = value
      else delete next[fieldKey]
      const typed = { ...base.typed, [sectionKey]: next }
      if (Object.keys(next).length === 0) delete typed[sectionKey]
      base.typed = typed
    }
  }

  const label = (identifier: string | { repository: string; path: string }) =>
    typeof identifier === 'string' ? identifier : sourceMappingKey(identifier)

  for (const raw of patch.addSourceMappings ?? []) {
    const mapping = asSourceMapping(raw as SourceMapping | string)
    const key = sourceMappingKey(mapping)
    const parent = inherited.sourceMappings.find((item) => sourceMappingKey(item.value) === key)
    const excludedHere = (base.excluded.sourceMappings ?? []).some((item) => item.toLowerCase() === key)
    if (parent && !excludedHere && !options.pinInherited) {
      errors[`addSourceMappings.${key}`] = inheritedNote(parent.source)
      continue
    }
    // Re-adding a mapping replaces it, so a kind or ref can be corrected.
    base.sourceMappings = [...base.sourceMappings.filter((item) => sourceMappingKey(item) !== key), mapping]
  }

  for (const identifier of patch.removeSourceMappings ?? []) {
    const own = base.sourceMappings.filter((item) => matchesSourceMapping(identifier, item))
    if (own.length === 0) {
      errors[`removeSourceMappings.${label(identifier)}`] = inherited.sourceMappings.some((item) =>
        matchesSourceMapping(identifier, item.value),
      )
        ? 'This mapping is inherited, not written at this scope; use excludeSourceMappings to suppress it here.'
        : 'This mapping is not written at this scope.'
      continue
    }
    base.sourceMappings = base.sourceMappings.filter((item) => !own.includes(item))
  }

  for (const identifier of patch.excludeSourceMappings ?? []) {
    const parents = inherited.sourceMappings.filter((item) => matchesSourceMapping(identifier, item.value))
    if (parents.length === 0) {
      errors[`excludeSourceMappings.${label(identifier)}`] = 'No inherited mapping matches, so there is nothing to exclude.'
      continue
    }
    const current = base.excluded.sourceMappings ?? []
    const keys = parents.map((item) => sourceMappingKey(item.value)).filter((key) => !current.includes(key))
    if (keys.length > 0) base.excluded = { ...base.excluded, sourceMappings: [...current, ...keys] }
  }

  for (const identifier of patch.restoreSourceMappings ?? []) {
    const current = base.excluded.sourceMappings ?? []
    const wanted = typeof identifier === 'string' ? identifier.trim().toLowerCase() : sourceMappingKey(identifier)
    // An exclusion is stored by key; a bare path matches any repository's mapping at that path.
    const next = current.filter((key) => key !== wanted && key.split('|')[1] !== wanted)
    if (next.length === current.length) {
      errors[`restoreSourceMappings.${label(identifier)}`] = 'This mapping is not excluded at this scope.'
      continue
    }
    const { sourceMappings: _previous, ...others } = base.excluded
    base.excluded = next.length > 0 ? { ...others, sourceMappings: next } : others
  }

  const parsed = structuredContextSchema.safeParse(base)
  if (!parsed.success) {
    for (const issue of parsed.error.issues) errors[issue.path.join('.') || 'context'] ??= issue.message
  }
  if (Object.keys(errors).length > 0 || !parsed.success) {
    throw new DomainError('validation', 'The context change is not valid.', errors)
  }
  return parsed.data
}
